import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Input, Switch, message } from 'antd';
import { GoogleOutlined } from '@ant-design/icons';
import { settingsApi } from '../../utils/api';

/**
 * Affiche et enregistre la connexion Google Sheets réservée au compte connecté.
 * L'utilisateur autorise directement son compte Google ; aucun JSON de clé de
 * service n'est demandé ni conservé dans le navigateur.
 */
const GoogleSheetsLeadsSettings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [googleConnected, setGoogleConnected] = useState(false);

  /** Charge l'état public de la connexion du seul utilisateur actuellement connecté. */
  const loadSettings = async () => {
    try {
      setLoading(true);
      const { data } = await settingsApi.getGoogleSheetsLeadsSettings();
      form.setFieldsValue(data);
      setGoogleConnected(Boolean(data.google_connected));
    } catch (error) {
      console.error('Erreur chargement Google Sheets Leads:', error);
      message.error('Impossible de charger votre connexion Google Sheets.');
    } finally {
      setLoading(false);
    }
  };

  /** Initialise le formulaire au montage pour afficher la connexion déjà enregistrée. */
  useEffect(() => {
    loadSettings();
  }, []);

  /** Enregistre le fichier ciblé avant de lancer l'autorisation Google du même compte. */
  const saveSettings = async (values) => {
    try {
      setSaving(true);
      const { data } = await settingsApi.updateGoogleSheetsLeadsSettings(values);
      form.setFieldsValue(data);
      setGoogleConnected(Boolean(data.google_connected));
      message.success('Fichier Google Sheets enregistré.');
    } catch (error) {
      console.error('Erreur sauvegarde Google Sheets Leads:', error);
      message.error(error.response?.data?.error || 'Impossible de sauvegarder la connexion Google Sheets.');
      throw error;
    } finally {
      setSaving(false);
    }
  };

  /** Ouvre l'autorisation OAuth Google après avoir sauvegardé le fichier choisi. */
  const connectGoogle = async () => {
    try {
      await form.validateFields();
      await saveSettings({ ...form.getFieldsValue(), enabled: false });
      const { data } = await settingsApi.getGoogleSheetsLeadsOAuthUrl();
      window.location.assign(data.url);
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.response?.data?.error || 'Impossible de démarrer la connexion Google.');
    }
  };

  /** Lance un contrôle non destructif du compte Google, de l'onglet et des colonnes Leads. */
  const testConnection = async () => {
    try {
      setTesting(true);
      const { data } = await settingsApi.testGoogleSheetsLeadsConnection();
      message.success(data.message);
    } catch (error) {
      console.error('Erreur test connexion Google Sheets:', error);
      message.error(error.response?.data?.error || 'Le test de connexion a échoué.');
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card title={<><GoogleOutlined /> Google Sheets — Leads Facebook</>} loading={loading}>
      <Alert
        type="info"
        showIcon
        className="mb-5"
        message="Cette connexion est propre à votre compte"
        description="Les commandes de votre boutique utilisent uniquement votre fichier Leads. Vous autorisez votre propre compte Google, sans fournir de fichier de clé."
      />
      <Form form={form} layout="vertical" onFinish={saveSettings}>
        <Form.Item name="enabled" label="Activer la synchronisation Lead → Qualifié → Converti" valuePropName="checked" extra={googleConnected ? 'Compte Google connecté.' : 'Connectez Google avant de pouvoir activer la synchronisation.'}>
          <Switch />
        </Form.Item>
        <Form.Item name="spreadsheet_id" label="ID du Google Sheet" rules={[{ required: true, message: 'L’ID du Google Sheet est obligatoire.' }]}>
          <Input placeholder="ID présent dans l’URL Google Sheets" />
        </Form.Item>
        <Form.Item name="sheet_name" label="Nom de l’onglet" initialValue="Leads" rules={[{ required: true, message: 'Le nom de l’onglet est obligatoire.' }]}>
          <Input placeholder="Leads" />
        </Form.Item>
        <Button className="mr-3" type="primary" onClick={connectGoogle} loading={saving}>Connecter mon compte Google</Button>
        <Button className="mr-3" onClick={testConnection} loading={testing}>Tester la connexion</Button>
        <Button htmlType="submit" loading={saving}>Enregistrer le fichier</Button>
      </Form>
    </Card>
  );
};

export default GoogleSheetsLeadsSettings;
